/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Ronnel Bedana - ronnel@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, ViewChild, ElementRef, Input, forwardRef, Output, EventEmitter} from '@angular/core';
import { SearchableProvider } from '../../../model/interface/searchable.provider';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputType } from 'src/app/model/enums/input.type';
import {LogService } from 'src/app/services';
import {CommaPipe} from "../../pipes/comma.pipe";

const noop = (): any => { };

@Component({
  selector: 'input-label',
  templateUrl: 'input-label.component.html',
  styleUrls: ['input-label.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputLabelComponent),
      multi: true
    }
  ],
})

export class InputLabelComponent implements ControlValueAccessor {
  @ViewChild('inputText') inputRef: ElementRef;
  @Input() provider: SearchableProvider;
  @Input() id: string;
  @Input() fieldName: string;
  @Input() type: InputType;
  @Input() allowZero = false;
  @Input() allowNegative = true;
  @Input() name: string;
  @Input() disabled = false;
  @Input() placeholder = 'Placeholder';
  @Input() inputValue: string;
  @Output() valueChange: EventEmitter<string> = new EventEmitter();

  editFlag = false;
  valueChangedFlag = false;

  private onChange: (_: any) => void = noop;
  private onTouched: () => void = noop;

  constructor(
    private currencyPipe: CommaPipe,
    private logService : LogService
  ) { }

  get value(): any {
    return this.inputValue;
  }

  set value(val) {
    this.inputValue = val;
    this.onChange(val);
  }

  registerOnChange(fn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn): void {
    this.onTouched = fn;
  }

  writeValue(value): void {
    if (this.type === InputType.MONEY) {
      this.inputValue = this.currencyPipe.transform(value);
    } else this.inputValue = value;
  }

  accept(): void {
    this.editFlag = false;
    this.placeholder = this.inputValue;

    if (this.provider) {
      this.provider.save(this.id, this.fieldName, this.inputValue)
        .subscribe(data => {
          if (data.success) {
            this.valueChangedFlag = false; // TODO: Implement proper closing

            // Propagate Changes to ngModel
            this.onChange(this.inputValue);
          } else this.logService.log(JSON.stringify(data, null, 2));
        });
    } else {
      this.valueChangedFlag = false;

      // Propagate Changes to ngModel
      this.onChange(this.inputValue);
    }

    // Emit Back
    this.valueChange.emit(this.inputValue);
  }

  /*
    * TODO: Shadow Buffering, see below
    * When components created and initial value is set, for example "123"
    * Save this initial value as "SHADOW COPY" somewhere.
    * If user cancels editing, revert value back to SHADOW COPY (previous value)
    *
    * If I change initial 123 to 333 and hit "clear", my value should be returned to 123
    * If I hit accept, value should be 333
    *
    * TODO: Have to implement 2 more behaviours:
    * - When user forgets to click "accept"
    * - Somewhere a global button "Accept on All Pending fields"
    * */
  clear(): void {
    this.valueChangedFlag = false;

    // Propagate Changes to ngModel
    this.onChange(this.inputValue);

    // Loose Focus
    if (this.inputRef.nativeElement) {
      // noinspection TypeScriptValidateJSTypes
      this.inputRef.nativeElement.blur();
    }
  }

  valueChanged(): void {
    this.valueChangedFlag = true;

    // Propagate Changes to ngModel
    this.onChange(this.inputValue);
  }

  onFocus(): void {
    if (this.type === InputType.MONEY) {
      if ((!this.allowZero ? this.inputValue === '0' : false)) {
        this.inputValue = '';
      } else {
        this.inputValue = this.currencyPipe.parse(this.inputValue);
        this.onChange(this.inputValue);
      }
    }
  }

  onBlur(): void {
    if (this.type === InputType.MONEY) {
      if (this.inputValue === '-' || this.inputValue === '' || (!this.allowZero ? this.inputValue === '0' : false)) {
        this.inputValue = '';
        this.onChange(null);
      } else {
        this.inputValue = <any>(Number(this.inputValue));
        this.onChange(this.inputValue);
        this.inputValue = this.currencyPipe.transform(Number(this.inputValue));
      }
    } else {
      this.onChange(this.inputValue);
    }

    this.accept();
  }

  onPaste(event: any): void {
    let data;

    if ((<any>window).clipboardData && (<any>window).clipboardData.getData) // IE
      data = (<any>window).clipboardData.getData('Text');
    else data = (event.originalEvent || event).clipboardData.getData('text/plain');

    const replacedValue = data.replace(/\D/gm, '');

    if (replacedValue !== '') {
      const numberValue = Number(replacedValue);
      this.inputValue = replacedValue;
      this.onChange(numberValue);
    }
    event.preventDefault();
  }

  onKeyPress(event: any): void {
    const pattern = /[0-9.]/;
    // eslint-disable-next-line no-useless-escape
    const patternFirst = /[0-9\-]/;
    const inputChar = String.fromCharCode(event.charCode);

    if (this.type === InputType.MONEY || this.type === InputType.NUMBER) {
      if ((this.inputValue + '').indexOf('.') > -1 && inputChar === '.') // Dirty Hack
        event.preventDefault();

      if (this.inputRef.nativeElement.selectionStart != 0 || !this.allowNegative) {
        if (!pattern.test(inputChar))
          event.preventDefault();
      } else if (this.allowNegative) {
        if (!patternFirst.test(inputChar))
          event.preventDefault();
      }
    }

    this.onChange(this.inputValue);
  }

  onLabelClick(): void {
    this.editFlag = true;

    if (!this.inputValue) {
      this.inputValue = this.placeholder;
      this.onChange(this.inputValue);
    }

    // Gain Focus
    setTimeout(() => {
      if (this.inputRef) {
        // noinspection TypeScriptValidateJSTypes
        this.inputRef.nativeElement.focus();
      }
    }, 100);
  }
}
